import { Link } from 'react-router-dom'
import {
  ETICHETE_ROL_PARTENER,
  ETICHETE_STARE,
  ROLURI_PARTENER,
  ceriPartener,
  type StareContainer,
} from '@b4l/shared'
import { SectiunePanou } from '../../ui/PanouLateral'
import { TON } from '../../ui/TonuriB4L'
import { despre, perioada } from './comun'
import { BaraBuget, IconMostre, bani, lei, semnale, type ActivitateCuFaza } from './activitate'

/**
 * Rezumatul unei activitati: ce e, ce cere atentie, cine, mostre, buget, teren. Aceeasi piesa in
 * panoul din lista si in dreapta paginii activitatii — o activitate arata la fel oriunde.
 */
export function RezumatActivitate({
  a,
  numeResponsabil,
}: {
  a: ActivitateCuFaza
  numeResponsabil: string | null
}) {
  const b = bani(a)
  const s = semnale(a)
  const fapte: [string, string | null][] = [
    ['Când', perioada(a)],
    ['Unde', [a.locatie, a.oras].filter(Boolean).join(', ') || null],
    ['Responsabil', numeResponsabil],
    ['Echipa', echipaPeScurt(a)],
    // Textul vechi, doar unde n-a devenit partener (vezi „Parteneri" mai jos).
    ['Organizator', a.parteneri?.some((p) => p.rol === 'organizator') ? null : a.organizator],
    ['Participanți estimați', a.participantiEstimati?.toString() ?? null],
    [
      'Contact',
      [a.contactNume, a.contactTelefon, a.contactEmail].filter(Boolean).join(' · ') || null,
    ],
    ['Stare', ETICHETE_STARE[a.status as StareContainer] ?? a.status],
  ]
  const descriere = a.ceAmAgreat || a.tema || a.observatii
  return (
    <>
      {descriere && (
        <p className="border-l-[3px] border-b4l-purple pl-2.5 text-corp text-b4l-text-muted">
          {descriere}
        </p>
      )}

      {s.length > 0 && (
        <div className="grid gap-1.5">
          {s.map((x) => (
            <div
              key={x.text}
              className={`rounded-lg px-3 py-2 text-mic ${TON[x.ton].pastila}`}
              title={x.detaliu}
            >
              <b className="font-semibold">{x.text}</b>
              {x.detaliu && <span className="opacity-80"> — {x.detaliu}</span>}
            </div>
          ))}
        </div>
      )}

      <Parteneri a={a} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {fapte
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="text-[11px] text-b4l-text-dim">{k}</dt>
              <dd className="break-words text-mic text-b4l-text">{v}</dd>
            </div>
          ))}
        <div>
          <dt className="text-[11px] text-b4l-text-dim">Mostre</dt>
          <dd>
            <IconMostre are={!!a.areMostre} cuText />
          </dd>
        </div>
      </dl>

      <SectiunePanou titlu="Buget">
        {b.buget ? (
          <div className="grid gap-2">
            <div className="flex items-baseline justify-between text-mic">
              <span className="text-b4l-text-muted">din {lei(b.buget)}</span>
              <span className="tabular-nums">
                <b className={b.disponibil! < 0 ? 'text-b4l-red-dark' : ''}>{lei(b.disponibil!)}</b>{' '}
                disponibil
              </span>
            </div>
            <BaraBuget a={a} inalta />
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-b4l-text-muted">
              <span>
                <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-b4l-blue" />
                Cheltuit <b className="text-b4l-text">{lei(b.cheltuit)}</b>
              </span>
              <span>
                <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-b4l-blue/35" />
                Angajat <b className="text-b4l-text">{lei(b.angajat)}</b>
              </span>
            </div>
          </div>
        ) : (
          <p className="text-mic text-b4l-text-muted">
            Fără buget{b.cheltuit ? ` · ${lei(b.cheltuit)} cheltuit` : ''}.
          </p>
        )}
      </SectiunePanou>

      <SectiunePanou titlu="Pe teren">
        <p className="text-mic text-b4l-text-muted">
          {a._count?.actiuniSampling
            ? `${a._count.actiuniSampling} ${a._count.actiuniSampling === 1 ? 'acțiune' : 'acțiuni'} pe teren`
            : 'Nicio acțiune pe teren.'}
        </p>
      </SectiunePanou>
    </>
  )
}

/**
 * „Ana Marin, Bogdan Ilie · 29–31 oct." — cine merge de la noi. Gol (rândul dispare) când nu merge
 * nimeni: alocarea e opțională.
 */
function echipaPeScurt(a: ActivitateCuFaza): string | null {
  const e = a.echipa
  if (!e || e.length === 0) return null
  const zile = e.flatMap((o) => o.zile.map((z) => z.zi.slice(0, 10))).sort()
  const zi = (s: string) =>
    new Date(`${s}T00:00:00Z`).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    })
  const cand =
    zile.length === 0
      ? ''
      : zile[0] === zile[zile.length - 1]
        ? zi(zile[0]!)
        : `${zi(zile[0]!)} – ${zi(zile[zile.length - 1]!)}`
  const nume = e.length <= 3 ? e.map((o) => o.user.name).join(', ') : `${e.length} colegi`
  return cand ? `${nume} · ${cand}` : nume
}

/**
 * Partenerii, grupați pe rol. Clic pe unul = lista filtrată pe el: toate activitățile la care a
 * participat, cu ce am cheltuit la fiecare.
 */
function Parteneri({ a }: { a: ActivitateCuFaza }) {
  const l = a.parteneri
  if (!l) return null
  if (l.length === 0)
    return ceriPartener(a.tip, a.generatAutomat) ? (
      <p className={`rounded-lg px-3 py-2 text-mic ${TON.galben.pastila}`}>
        <b className="font-semibold">Fără partener încă</b> — adaugă-l din „Editează” când se
        concretizează. E necesar înainte de a lega o factură.
      </p>
    ) : null
  return (
    <div className="grid gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
        Parteneri
      </span>
      {ROLURI_PARTENER.filter((r) => l.some((p) => p.rol === r)).map((r) => (
        <div key={r} className="grid gap-0.5">
          <span className="text-[11px] text-b4l-text-dim">{ETICHETE_ROL_PARTENER[r]}</span>
          {l
            .filter((p) => p.rol === r)
            .map((p) => {
              const d = despre(p)
              return (
                <span key={p.id} className="text-mic text-b4l-text">
                  <Link
                    to={`/marketing/evenimente?partener=${d.cheie}`}
                    className="font-medium text-b4l-blue-dark hover:underline"
                  >
                    {d.nume}
                  </Link>
                  {p.nota && <span className="text-b4l-text-dim"> · {p.nota}</span>}
                </span>
              )
            })}
        </div>
      ))}
    </div>
  )
}
